/**
 * Client-side batching for journey BACKFILL (shared/sessions/journey-backfill.ts).
 *
 * Replayed fixes — captured natively while the WebView was asleep and drained
 * on resume — arrive as a burst, oldest first. Instead of pinging each one
 * (which would also move the member's live position onto minutes-old GPS),
 * `useEnRouteGpsTracking` hands them here; the queue coalesces the burst,
 * downsamples it to the live ping cadence by fix timestamp/position, and
 * POSTs oldest-first in 60-fix chunks. `send` is told `{ isLast }` so the
 * caller can `setJourney` only after the last chunk (dwell must not arm on
 * a mid-flush arrival). Serialised so a slow network cannot interleave two
 * flushes.
 *
 * Kept outside the hook so the hook edit is a single branch at the top of its
 * watch callback (the journey-progress overhaul rewrites the hook's cadence).
 */

import {
  downsampleBackfillFixes,
  JOURNEY_BACKFILL_MAX_PER_REQUEST,
  toBackfillFix,
  type JourneyBackfillFix,
} from '@shared/sessions/journey-backfill'
import type { GeoStamp } from '@shared/sessions/types'

/** A drained burst lands within a few ms; this coalesces it into one request. */
export const BACKFILL_FLUSH_DELAY_MS = 750

export interface JourneyBackfillSendMeta {
  /** True for the last chunk of this `flushNow` loop — `setJourney` only then. */
  isLast: boolean
}

export interface JourneyBackfillQueue {
  /** Enqueue a replayed fix (one whose own timestamp failed the live-age gate). */
  push(fix: GeoStamp): void
  /** Send whatever is queued now (also called automatically after the delay). */
  flush(): Promise<void>
  /** Cancel the pending flush and drop queued fixes (unmount / journey over). */
  dispose(): void
  /** Queued fixes not yet sent, plus 1 while a flush POST is in flight. */
  readonly pending: number
}

export function createJourneyBackfillQueue(options: {
  send: (fixes: JourneyBackfillFix[], meta: JourneyBackfillSendMeta) => Promise<void>
  flushDelayMs?: number
  maxPerRequest?: number
}): JourneyBackfillQueue {
  const delay = options.flushDelayMs ?? BACKFILL_FLUSH_DELAY_MS
  const max = options.maxPerRequest ?? JOURNEY_BACKFILL_MAX_PER_REQUEST
  let queued: GeoStamp[] = []
  let timer: ReturnType<typeof setTimeout> | null = null
  let inFlight: Promise<void> | null = null
  let disposed = false

  const flushNow = async (): Promise<void> => {
    // Loop until the coalesced burst (plus anything that arrived during a
    // send) is gone. Downsample first (cadence + drop mock/coarse), then
    // POST oldest-first in `max`-sized requests so a long lock stretch is
    // not thinned by dropping the oldest points.
    while (!disposed && queued.length > 0) {
      const downsampled = downsampleBackfillFixes(queued)
      queued = []
      if (downsampled.length === 0) return
      for (let i = 0; i < downsampled.length; i += max) {
        if (disposed) return
        const batch = downsampled.slice(i, i + max)
        const moreInThis = i + max < downsampled.length
        try {
          await options.send(batch.map(toBackfillFix), {
            // Last slice of this downsample, and nothing new queued during
            // earlier sends of this loop. A push during THIS send can make
            // the outer while run again — that later chunk is also last.
            isLast: !moreInThis && queued.length === 0,
          })
        } catch {
          // Best-effort: the route is a nice-to-have on top of the live journey.
          // This chunk is not re-queued (it would be re-rejected as
          // duplicates/stale if the server did land it) — remaining chunks
          // still go out so older fixes are not dropped to keep the newest.
        }
      }
    }
  }

  const schedule = () => {
    if (timer != null || disposed) return
    timer = setTimeout(() => {
      timer = null
      void queue.flush()
    }, delay)
  }

  const queue: JourneyBackfillQueue = {
    push(fix) {
      if (disposed) return
      queued.push(fix)
      schedule()
    },
    async flush() {
      if (timer != null) {
        clearTimeout(timer)
        timer = null
      }
      // Serialise: a flush that arrives while one is in flight waits for it,
      // then sends whatever accumulated meanwhile.
      const previous = inFlight ?? Promise.resolve()
      const run = previous.then(flushNow, flushNow)
      inFlight = run
      try {
        await run
      } finally {
        if (inFlight === run) inFlight = null
      }
    },
    dispose() {
      disposed = true
      if (timer != null) {
        clearTimeout(timer)
        timer = null
      }
      queued = []
    },
    get pending() {
      return queued.length + (inFlight ? 1 : 0)
    },
  }
  return queue
}
